'use client'

import { useState, useEffect } from 'react'
import { useTheme } from '@/components/ThemeProvider'
import Image from 'next/image'
import { useParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Link, usePathname, useRouter } from '@/navigation'
import { Moon, Sun, Menu, X } from 'lucide-react'
import { pushGTMEvent } from '@/lib/gtm'

export default function Header() {
  const t = useTranslations('Header')
  const { theme, resolvedTheme, setTheme } = useTheme()
  const locale = useLocale()
  const router = useRouter()
  const pathname = usePathname()
  const params = useParams()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  // Previne descompasso de hidratação (hydration mismatch)
  useEffect(() => {
    setMounted(true)
  }, [])

  // Troca de idioma via URL e disparo de Analytics
  const handleLanguageChange = (newLocale: string) => {
    pushGTMEvent({
      event: 'language_change',
      from_locale: locale,
      to_locale: newLocale,
      pathname
    })
    const { locale: _locale, ...routeParams } = params
    router.replace(
      // pathname é a rota interna (ex.: '/blog/[slug]'); os params preenchem os segmentos dinâmicos
      { pathname, params: routeParams } as never,
      { locale: newLocale },
    )
  }

  // Alternância de Tema e disparo de Analytics
  const handleThemeToggle = () => {
    const currentTheme = theme === 'system' ? resolvedTheme : theme
    const nextTheme = currentTheme === 'dark' ? 'light' : 'dark'

    pushGTMEvent({
      event: 'theme_change',
      theme: nextTheme
    })

    setTheme(nextTheme)
  }

  const isDark = mounted && resolvedTheme === 'dark'

  return (
    <header className="sticky top-0 z-40 border-b border-current/10 bg-background/90 backdrop-blur-md transition-colors">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 md:px-8">
        {/* Logo Acessível */}
        <Link
          href="/"
          className="flex items-center transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-[#718d00]"
          aria-label="WLC. Wesley Luiz Carvalho, página inicial"
        >
          <Image src="/logo-wlc-light.webp" alt="" width={560} height={197} className="h-7 w-auto dark:hidden" />
          <Image src="/logo-wlc-dark.webp" alt="" width={560} height={197} className="hidden h-7 w-auto dark:block" />
        </Link>

        {/* Navegação Desktop */}
        <nav className="hidden items-center gap-6 text-base font-medium md:flex" aria-label="Navegação principal">
          <Link href={{ pathname: '/', hash: 'sobre' }} data-track="nav_click" data-track-name="sobre" className="relative rounded px-2 py-2 after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:bg-accent-green after:transition-transform after:duration-300 hover:after:scale-x-100 focus-visible:after:scale-x-100 focus-visible:ring-2 focus-visible:ring-[#718d00]">{t('about')}</Link>
          <Link href={{ pathname: '/', hash: 'experiencia' }} data-track="nav_click" data-track-name="experiencia" className="relative rounded px-2 py-2 after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:bg-accent-green after:transition-transform after:duration-300 hover:after:scale-x-100 focus-visible:after:scale-x-100 focus-visible:ring-2 focus-visible:ring-[#718d00]">{t('experience')}</Link>
          <Link href={{ pathname: '/', hash: 'servicos' }} data-track="nav_click" data-track-name="servicos" className="relative rounded px-2 py-2 after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:bg-accent-green after:transition-transform after:duration-300 hover:after:scale-x-100 focus-visible:after:scale-x-100 focus-visible:ring-2 focus-visible:ring-[#718d00]">{t('services')}</Link>
          <Link href={{ pathname: '/', hash: 'trabalhos' }} data-track="nav_click" data-track-name="trabalhos" className="relative rounded px-2 py-2 after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:bg-accent-green after:transition-transform after:duration-300 hover:after:scale-x-100 focus-visible:after:scale-x-100 focus-visible:ring-2 focus-visible:ring-[#718d00]">{t('work')}</Link>
          <Link href={{ pathname: '/', hash: 'contato' }} data-track="nav_click" data-track-name="contato" className="relative rounded px-2 py-2 after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:bg-accent-green after:transition-transform after:duration-300 hover:after:scale-x-100 focus-visible:after:scale-x-100 focus-visible:ring-2 focus-visible:ring-[#718d00]">{t('contact')}</Link>
        </nav>

        {/* Controles de Acessibilidade: Idioma e Tema */}
        <div className="flex items-center gap-2">
          {/* Seletor de Idioma com Label acessível */}
          <div className="relative inline-block">
            <label htmlFor="language-select" className="sr-only">{locale === 'pt' ? 'Selecione o idioma' : locale === 'es' ? 'Seleccionar idioma' : 'Select language'}</label>
            <select
              id="language-select"
              value={locale}
              aria-label={locale === 'pt' ? 'Selecione o idioma da página' : locale === 'es' ? 'Seleccionar idioma de la página' : 'Select page language'}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="rounded-full border border-current/20 bg-transparent px-3 py-1.5 text-xs font-semibold uppercase tracking-wider outline-none cursor-pointer hover:bg-current/5 transition focus-visible:ring-2 focus-visible:ring-[#718d00]"
            >
              <option value="pt" className="bg-background text-foreground">PT</option>
              <option value="en" className="bg-background text-foreground">EN</option>
              <option value="es" className="bg-background text-foreground">ES</option>
            </select>
          </div>

          {/* Botão de Tema com aria-label dinâmico e ícones com aria-hidden */}
          <button
            type="button"
            aria-label={!mounted ? 'Carregando tema' : isDark ? 'Alternar para tema claro' : 'Alternar para tema escuro'}
            onClick={handleThemeToggle}
            className="rounded-full border border-current/20 p-2 transition hover:bg-current/10 flex items-center justify-center size-8 focus-visible:ring-2 focus-visible:ring-[#718d00]"
          >
            {!mounted ? (
              <span className="size-4 block opacity-0" aria-hidden="true" />
            ) : isDark ? (
              <Sun className="size-4 text-[#d8f36b]" aria-hidden="true" />
            ) : (
              <Moon className="size-4 text-[#151515]" aria-hidden="true" />
            )}
          </button>

          {/* Botão do Menu Mobile com aria-expanded */}
          <button
            type="button"
            aria-label={menuOpen ? 'Fechar menu de navegação' : 'Abrir menu de navegação'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
            className="rounded-full border border-current/20 p-2 md:hidden focus-visible:ring-2 focus-visible:ring-[#718d00]"
          >
            {menuOpen ? <X className="size-5" aria-hidden="true" /> : <Menu className="size-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Gaveta do Menu Mobile */}
      {menuOpen && (
        <nav className="flex flex-col gap-4 border-t border-current/10 px-6 py-5 text-base md:hidden bg-background" aria-label="Menu móvel">
          <Link href={{ pathname: '/', hash: 'sobre' }} data-track="nav_click" data-track-name="sobre" onClick={() => setMenuOpen(false)} className="w-fit underline-offset-8 decoration-accent-green decoration-2 hover:underline focus-visible:underline">{t('about')}</Link>
          <Link href={{ pathname: '/', hash: 'experiencia' }} data-track="nav_click" data-track-name="experiencia" onClick={() => setMenuOpen(false)} className="w-fit underline-offset-8 decoration-accent-green decoration-2 hover:underline focus-visible:underline">{t('experience')}</Link>
          <Link href={{ pathname: '/', hash: 'servicos' }} data-track="nav_click" data-track-name="servicos" onClick={() => setMenuOpen(false)} className="w-fit underline-offset-8 decoration-accent-green decoration-2 hover:underline focus-visible:underline">{t('services')}</Link>
          <Link href={{ pathname: '/', hash: 'trabalhos' }} data-track="nav_click" data-track-name="trabalhos" onClick={() => setMenuOpen(false)} className="w-fit underline-offset-8 decoration-accent-green decoration-2 hover:underline focus-visible:underline">{t('work')}</Link>
          <Link href={{ pathname: '/', hash: 'contato' }} data-track="nav_click" data-track-name="contato" onClick={() => setMenuOpen(false)} className="w-fit underline-offset-8 decoration-accent-green decoration-2 hover:underline focus-visible:underline">{t('contact')}</Link>
        </nav>
      )}
    </header>
  )
}
